import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { datadogRum } from '@datadog/browser-rum';
import { useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  Dashboard,
  Summary,
  getAvailableOptionsForOrder,
  getDashboard,
  getSummary
} from '|/app/services';
import AppFrameworkContext from '|/helpers/app-framework';
import useAbortController from '../../helpers/use-abort-controller';
import { NavigationState } from '../navigation-state';

const useNextStepVm = (
  UpdateDashboardAndSummary: (
    newDashboard: Dashboard,
    newSummary: Summary,
    newAvailableOptions: any
  ) => void
) => {
  const { abortController } = useAbortController();
  const { load } = useContext(AppFrameworkContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { previousPath = '' } = (location.state as NavigationState) || {};
  useEffect(() => {
    const getDashboardPromise = getDashboard(abortController.signal);
    const getSummaryPromise = getSummary(abortController.signal);
    const getAvailableOptionsPromise = getAvailableOptionsForOrder(
      abortController.signal
    );

    load(
      Promise.all([
        getDashboardPromise,
        getSummaryPromise,
        getAvailableOptionsPromise
      ]).then(([dashboard, summary, availableOptions]) => {
        if (dashboard === null || summary === null) {
          navigate('/vehicle/missing', { replace: true });
          return;
        }
        UpdateDashboardAndSummary(dashboard, summary, availableOptions);
        const {
          vehicle: { isAvailable = false, id = null } = {},
          totals: { vehicleStickerPrice = 0 } = {}
        } = summary;
        if (isAvailable !== true) {
          navigate(`/vehicle/unavailable/${id}`, { replace: true });
          return;
        }
        if (vehicleStickerPrice != null && vehicleStickerPrice < 6000) {
          navigate('/vehicle/error-price', { replace: true });
          return;
        }
        const { steps = [] } = dashboard || {};
        if (steps.length === 0) {
          return;
        }
        const orderedSteps = steps.sort((a, b) => a.order - b.order);
        const activeStep = orderedSteps.find(
          step => step.isEnabled && !step.isComplete
        );

        const firstStepName = orderedSteps[0];

        const nextStep = activeStep?.url || firstStepName?.url || '';

        const newPath = `/steps/${nextStep}`;
        if (previousPath.startsWith(newPath)) {
          datadogRum.addError(new Error('step loop detected'), {
            customType: 'stepLooping',
            stepUrl: newPath,
            previousStepUrl: previousPath,
            activeStep,
            firstStepName
          });
        } else if (previousPath !== '') {
          const [
            ignoredLead,
            ignoredStepsFolder,
            stepName,
            ...ignoredSubPages
          ] = previousPath.split('/');
          trackEvent('pp_step_complete', {
            stepName,
            nextStep
          });
        }

        navigate(`/steps/${nextStep}`, { replace: true });
      }),
      abortController.signal
    );
  }, [
    UpdateDashboardAndSummary,
    load,
    previousPath,
    abortController.signal,
    trackEvent,
    navigate
  ]);
};

export default useNextStepVm;
